import { uuid } from '@oskecare/fe-utilities/scripts/uuid';
import request from '@oskecare/fe-utilities/lib/request';
import { USAGE_CONSUMPTION_NEW } from '@oskecare/fe-utilities/lib/constants/dataSources';
import { cacheWithErrorCheck } from '../../helpers.js';
import ConsumptionReportList from './models/ConsumptionReportList.js';
import ConsumptionPurchaseHistoryEntry from './models/ConsumptionPurchaseHistoryEntry.js';

const LOG_SOURCE = 'infra-consumption';

/**
 * Infrastructure module for providing consumption related data.
 *
 * @property {Logger} _logger
 * @property {CustomerDataManager} _customerDataManager
 * @property {Map<string, Promise<*>>} _cache
 * @property {string} _consumptionApiUrl
 */
export default class Consumption {
  /**
   * @param {Logger} logger
   * @param {CustomerDataManager} customerDataManager
   * @param {object} config
   */
  constructor({ logger, customerDataManager, config }) {
    this._logger = logger;
    this._customerDataManager = customerDataManager;
    this._cache = new Map();
    this._consumptionApiUrl = config.consumptionApiUrl;
  }

  /**
   * @param {string} sn
   * @param {boolean} isNonessential
   * @return {Promise<ConsumptionReport>}
   */
  async getCurrentUsageConsumptionReport(sn, isNonessential = false) {
    const { isPrepaid } = (await this._customerDataManager.getCustomerData()).cnData;
    return cacheWithErrorCheck(this._cache, `currentUsageConsumptionReport-${sn}`, () =>
      request({
        method: 'GET',
        route: `${this._consumptionApiUrl}/usageConsumptionReport`,
        query: { publicKey: sn },
        headers: {
          'osk-message-id': uuid(),
          'osk-transaction-id': `${Date.now()}-${sn}`,
        },
        logSource: LOG_SOURCE,
        dataSource: USAGE_CONSUMPTION_NEW,
        isNonessential,
      })
        .then(data => {
          try {
            const reportList = new ConsumptionReportList(sn, data, isPrepaid);
            const report = reportList.currentConsumptionReport;
            const issues = report.issuesLog;
            if (issues) {
              this._logger.warning(issues, LOG_SOURCE);
            }
            this._logger.debug(report.bucketLog, LOG_SOURCE);
            return report;
          } catch (error) {
            error.logSource = LOG_SOURCE;
            throw error;
          }
        })
        .catch(this._logger.catchError),
    );
  }

  /**
   * @param {string} sn
   * @param {boolean} isNonessential
   * @param {?Date} startDate
   * @param {?Date} endDate
   * @return {Promise<ConsumptionReportList>}
   */
  async getPreviousUsageConsumptionReports(
    sn,
    isNonessential = false,
    startDate = null,
    endDate = null,
  ) {
    const { isPrepaid } = (await this._customerDataManager.getCustomerData()).cnData;
    const defaultStartDate = new Date();
    defaultStartDate.setMonth(defaultStartDate.getMonth() - 6);
    const defaultEndDate = new Date();
    if (isPrepaid) {
      defaultEndDate.setDate(0);
      defaultEndDate.setHours(0, 0, 0, 0);
    }
    return cacheWithErrorCheck(this._cache, `previousUsageConsumptionReports-${sn}`, () =>
      request({
        method: 'GET',
        route: `${this._consumptionApiUrl}/usageConsumptionReport`,
        query: {
          publicKey: sn,
          dateFrom: (startDate ?? defaultStartDate).toISOString().replace(/T.*$/, ''),
          dateTo: (endDate ?? defaultEndDate).toISOString().replace(/T.*$/, ''),
        },
        headers: {
          'osk-message-id': uuid(),
          'osk-transaction-id': `${Date.now()}-${sn}`,
        },
        logSource: LOG_SOURCE,
        dataSource: USAGE_CONSUMPTION_NEW,
        isNonessential,
      })
        .then(data => {
          try {
            const reportList = new ConsumptionReportList(sn, data, isPrepaid);
            reportList.previousConsumptionReports.forEach(report => {
              const issues = report.issuesLog;
              if (issues) {
                this._logger.warning(issues, LOG_SOURCE);
              }
              this._logger.debug(report.bucketLog, LOG_SOURCE);
            });
            return reportList;
          } catch (error) {
            error.logSource = LOG_SOURCE;
            throw error;
          }
        })
        .catch(this._logger.catchError),
    );
  }

  /**
   * @param {string} sn
   * @param {boolean} isNonessential
   * @return {Promise<ConsumptionPurchaseHistoryEntry[]>}
   */
  async getPreviousConsumptionPurchaseHistory(sn, isNonessential = false) {
    return cacheWithErrorCheck(this._cache, `consumptionPurchaseHistory-${sn}`, () =>
      request({
        method: 'GET',
        route: `${this._consumptionApiUrl}/resourceOrder`,
        query: { publicKey: sn },
        headers: {
          osk_message_id: uuid(),
          osk_transaction_id: `${Date.now()}-${sn}`,
        },
        logSource: LOG_SOURCE,
        dataSource: USAGE_CONSUMPTION_NEW,
        isNonessential,
      })
        .then(purchaseHistory =>
          purchaseHistory.map(
            historyEntryData => new ConsumptionPurchaseHistoryEntry(historyEntryData),
          ),
        )
        .catch(this._logger.catchError),
    );
  }

  /**
   * @return {Promise<string>}
   */
  async getConsumptionView() {
    const { sn } = (await this._customerDataManager.getCustomerData()).loggedInSnData;
    return (await this.getCurrentUsageConsumptionReport(sn)).consumptionView;
  }

  /**
   * @param {boolean} isNonessential
   * @return {Promise<boolean>}
   */
  async getShowRemaining(isNonessential = false) {
    const { sn } = (await this._customerDataManager.getCustomerData()).loggedInSnData;
    return (await this.getCurrentUsageConsumptionReport(sn, isNonessential)).showRemaining;
  }

  /**
   * Resets whole consumption cache including previous and current consumption.
   */
  resetCache() {
    this._cache.clear();
  }
}
